import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileText, Loader2, FolderOpen, Tag, X, Edit3, Check, Square, CheckSquare } from "lucide-react";

function fmtBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"]; let i = 0; let x = Number(n);
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
function fmtDate(iso) { if (!iso) return ""; return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }

function DocumentFilters({ filter, setFilter, clients, folders, allTags, count }) {
  return (
    <div className="flex flex-wrap gap-3 items-center mb-5" data-testid="documents-filter">
      <label className="overline text-[10px]">Filter</label>
      <select
        value={filter.client_id}
        onChange={(e) => setFilter({ ...filter, client_id: e.target.value })}
        data-testid="documents-filter-client"
        className="bg-[var(--paper)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px]"
      >
        <option value="">All clients</option>
        {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
      </select>
      <select
        value={filter.folder === null ? "__any" : filter.folder}
        onChange={(e) => setFilter({ ...filter, folder: e.target.value === "__any" ? null : e.target.value })}
        data-testid="documents-filter-folder"
        className="bg-[var(--paper)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px]"
      >
        <option value="__any">All folders</option>
        <option value="">No folder</option>
        {folders.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
      <select
        value={filter.tag}
        onChange={(e) => setFilter({ ...filter, tag: e.target.value })}
        data-testid="documents-filter-tag"
        className="bg-[var(--paper)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px]"
      >
        <option value="">All tags</option>
        {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <div className="text-[12px] text-[var(--ink-3)] font-mono ml-auto">{count} files</div>
    </div>
  );
}

function DocumentRow({ d, selected, clientName, filter, setFilter, onToggle, onEdit, onDownload, onRemove }) {
  return (
    <tr data-testid={`document-row-${d.document_id}`}
        className={selected ? "bg-[var(--subtle)]" : ""}>
      <td>
        <button onClick={() => onToggle(d.document_id)} data-testid={`document-select-${d.document_id}`}
          className="text-[var(--ink-3)] hover:text-[var(--ink)]">
          {selected ? <CheckSquare size={14} /> : <Square size={14} />}
        </button>
      </td>
      <td>
        <div className="font-medium">{d.filename}</div>
        <div className="text-[11px] text-[var(--ink-3)] font-mono">{d.content_type}</div>
      </td>
      <td className="text-[12px]">
        {d.folder ? (
          <button onClick={() => setFilter({ ...filter, folder: d.folder })}
            data-testid={`document-folder-${d.document_id}`}
            className="inline-flex items-center gap-1 text-[var(--ink-2)] hover:text-[var(--ink)]">
            <FolderOpen size={11} /> {d.folder}
          </button>
        ) : <span className="text-[var(--ink-3)] font-mono">—</span>}
      </td>
      <td>
        <div className="flex flex-wrap gap-1" data-testid={`document-tags-${d.document_id}`}>
          {(d.tags || []).map((t, idx) => (
            <button key={`${d.document_id}-${t}-${idx}`} onClick={() => setFilter({ ...filter, tag: t })}
              className="text-[10px] px-1.5 py-0.5 rounded-full border border-[var(--border)] hover:border-[var(--ink)] inline-flex items-center gap-1">
              <Tag size={9} /> {t}
            </button>
          ))}
          {(d.tags || []).length === 0 && <span className="text-[var(--ink-3)] font-mono text-[11px]">—</span>}
        </div>
      </td>
      <td>{d.client_id ? clientName(d.client_id) : "—"}</td>
      <td className="font-mono text-[12px]">{fmtBytes(d.size_bytes)}</td>
      <td>
        <span className="badge badge-outline">
          {d.uploaded_by_client_id ? "client upload" : "firm upload"}
        </span>
      </td>
      <td className="text-[12px] text-[var(--ink-3)] font-mono">{fmtDate(d.created_at)}</td>
      <td className="text-right">
        <div className="flex justify-end gap-3">
          <button onClick={() => onEdit(d)} data-testid={`document-edit-${d.document_id}`}
            className="text-[11px] inline-flex items-center gap-1 text-[var(--ink-2)] hover:text-[var(--ink)]">
            <Edit3 size={11} /> Edit
          </button>
          <button onClick={() => onDownload(d)} data-testid={`document-download-${d.document_id}`}
            className="text-[11px] inline-flex items-center gap-1 text-[var(--ink-2)] hover:text-[var(--ink)]">
            <Download size={11} /> Download
          </button>
          <button onClick={() => onRemove(d)} data-testid={`document-delete-${d.document_id}`}
            className="text-[11px] inline-flex items-center gap-1 text-[var(--ink-3)] hover:text-[var(--accent)]">
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function Documents() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [folders, setFolders] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState({ client_id: "", folder: null, tag: "" });
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter.client_id) params.append("client_id", filter.client_id);
    if (filter.folder !== null) params.append("folder", filter.folder);
    if (filter.tag) params.append("tag", filter.tag);
    const [d, c, f, t] = await Promise.all([
      axios.get(`${API_BASE}/documents${params.toString() ? `?${params}` : ""}`),
      axios.get(`${API_BASE}/clients`),
      axios.get(`${API_BASE}/documents/folders`),
      axios.get(`${API_BASE}/documents/tags`),
    ]);
    setRows(d.data); setClients(c.data);
    setFolders(f.data.folders || []); setAllTags(t.data.tags || []);
    setLoading(false);
  };
  useEffect(() => { reload(); }, [filter.client_id, filter.folder, filter.tag]);  // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (file, opts = {}) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (opts.client_id) fd.append("client_id", opts.client_id);
      if (opts.visibility) fd.append("visibility", opts.visibility);
      if (opts.folder) fd.append("folder", opts.folder);
      if (opts.tags) fd.append("tags", opts.tags);
      await axios.post(`${API_BASE}/documents/upload`, fd);
      toast.success(`${file.name} uploaded`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const download = async (doc) => {
    const url = `${API_BASE}/documents/${doc.document_id}/download`;
    try {
      const r = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: doc.content_type });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = doc.filename; a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_BASE}/documents/${doc.document_id}`);
      toast.success("Deleted");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clientName = (id) => clients.find((c) => c.client_id === id)?.name || "—";

  const toggleOne = (id) => setSelected((s) => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => setSelected((s) => {
    if (s.size === rows.length) return new Set();
    return new Set(rows.map((r) => r.document_id));
  });
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action, value) => {
    if (selected.size === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${selected.size} file(s)? Cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/documents/bulk`, {
        document_ids: Array.from(selected), action, value,
      });
      toast.success(`${r.data?.modified ?? 0} files updated`);
      clearSelection();
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="documents-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline mb-2">Documents</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>All files</h1>
          <p className="text-[var(--ink-2)] mt-2">Files uploaded by your team and by clients via file requests.</p>
        </div>
        <UploadButton uploading={uploading} clients={clients} onUpload={upload} />
      </div>

      <DocumentFilters filter={filter} setFilter={setFilter} clients={clients}
                       folders={folders} allTags={allTags} count={rows.length} />

      {selected.size > 0 && (
        <BulkToolbar
          count={selected.size}
          folders={folders}
          allTags={allTags}
          busy={bulkBusy}
          onRun={runBulk}
          onClear={clearSelection}
        />
      )}

      {loading ? <div className="text-[var(--ink-3)]">Loading…</div>
        : rows.length === 0 ? (
        <div className="card p-10 text-center" data-testid="documents-empty">
          <FileText size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No files yet</div>
          <div className="text-[var(--ink-3)] text-sm">Upload one above, or wait for a client to fulfil a file request.</div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="report-table">
            <thead><tr>
              <th style={{ width: 40 }}>
                <button onClick={toggleAll} data-testid="documents-select-all"
                  className="inline-flex items-center text-[var(--ink-3)] hover:text-[var(--ink)]"
                  title={selected.size === rows.length ? "Clear all" : "Select all"}>
                  {selected.size === rows.length && rows.length > 0
                    ? <CheckSquare size={14} />
                    : <Square size={14} />}
                </button>
              </th>
              <th>File</th><th>Folder</th><th>Tags</th><th>Client</th><th>Size</th><th>Source</th><th>Uploaded</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((d) => (
                <DocumentRow
                  key={d.document_id}
                  d={d}
                  selected={selected.has(d.document_id)}
                  clientName={clientName}
                  filter={filter}
                  setFilter={setFilter}
                  onToggle={toggleOne}
                  onEdit={setEditing}
                  onDownload={download}
                  onRemove={remove}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditDocumentModal
          doc={editing}
          clients={clients}
          folders={folders}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function EditDocumentModal({ doc, clients, folders, onClose, onSaved }) {
  const [folder, setFolder] = useState(doc.folder || "");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState(doc.tags || []);
  const [visibility, setVisibility] = useState(doc.visibility || "firm");
  const [clientId, setClientId] = useState(doc.client_id || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || tags.includes(t)) return;
    setTags([...tags, t]); setTagInput("");
  };
  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await axios.patch(`${API_BASE}/documents/${doc.document_id}`, {
        folder, tags, visibility, client_id: clientId || null,
      });
      toast.success("Document updated");
      onSaved();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(26,24,20,0.45)" }} data-testid="document-edit-modal">
      <div className="bg-[var(--paper)] border border-[var(--border)] rounded-md max-w-lg w-full p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline">Edit document</div>
            <h3 className="font-heading text-2xl mt-1 truncate max-w-[420px]" style={{ fontWeight: 500 }}>{doc.filename}</h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="document-edit-close"><X size={20} /></button>
        </div>

        <form onSubmit={save} className="space-y-4" data-testid="document-edit-form">
          <div>
            <label className="overline block mb-1.5">Folder</label>
            <input list="folder-suggestions" value={folder} onChange={(e) => setFolder(e.target.value)}
              data-testid="document-edit-folder"
              placeholder="e.g. Tax 2026"
              className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px]" />
            <datalist id="folder-suggestions">
              {folders.map((f) => <option key={f} value={f} />)}
            </datalist>
          </div>
          <div>
            <label className="overline block mb-1.5">Tags</label>
            <div className="flex flex-wrap gap-1 mb-2" data-testid="document-edit-tag-list">
              {tags.map((t) => (
                <button type="button" key={t} onClick={() => removeTag(t)}
                  className="text-[11px] px-1.5 py-0.5 rounded-full bg-[var(--subtle)] border border-[var(--border)] inline-flex items-center gap-1 hover:border-[var(--accent)] hover:text-[var(--accent)]">
                  <Tag size={9} /> {t} <X size={9} />
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                data-testid="document-edit-tag-input"
                placeholder="Add a tag and press Enter"
                className="flex-1 bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px]" />
              <button type="button" onClick={addTag} className="btn-secondary" data-testid="document-edit-tag-add">Add</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1.5">Client</label>
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="document-edit-client"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px]">
                <option value="">No client</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-1.5">Visibility</label>
              <select value={visibility} onChange={(e) => setVisibility(e.target.value)} data-testid="document-edit-visibility"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px]">
                <option value="firm">Internal</option>
                <option value="client">Share with client</option>
              </select>
            </div>
          </div>

          {err && <div className="text-[13px] text-[var(--accent)]" data-testid="document-edit-error">{err}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1 justify-center" data-testid="document-edit-save">
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}{busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkToolbar({ count, folders, allTags, busy, onRun, onClear }) {
  const [folderVal, setFolderVal] = useState("");
  const [tagVal, setTagVal] = useState("");

  return (
    <div className="card p-3 mb-4 flex flex-wrap items-center gap-3 sticky top-2 z-10" data-testid="documents-bulk-toolbar"
         style={{ borderColor: "var(--ink)" }}>
      <div className="flex items-center gap-2">
        <Check size={14} style={{ color: "var(--accent)" }} />
        <span className="text-[13px] font-medium" data-testid="documents-bulk-count">{count} selected</span>
      </div>

      <div className="flex items-center gap-1.5 ml-2">
        <input list="bulk-folder-list" placeholder="Folder name" value={folderVal}
          onChange={(e) => setFolderVal(e.target.value)}
          data-testid="documents-bulk-folder-input"
          className="bg-white border border-[var(--border)] rounded-md px-2 py-1.5 text-[12px] w-32" />
        <datalist id="bulk-folder-list">{folders.map((f) => <option key={f} value={f} />)}</datalist>
        <button onClick={() => onRun("move_folder", folderVal)} disabled={busy} className="btn-secondary"
                data-testid="documents-bulk-move-folder"
                title={folderVal ? `Move to "${folderVal}"` : "Clear folder"}>
          {folderVal ? "Move →" : "Clear folder"}
        </button>
      </div>

      <div className="flex items-center gap-1.5">
        <input list="bulk-tag-list" placeholder="Tag" value={tagVal}
          onChange={(e) => setTagVal(e.target.value)}
          data-testid="documents-bulk-tag-input"
          className="bg-white border border-[var(--border)] rounded-md px-2 py-1.5 text-[12px] w-28" />
        <datalist id="bulk-tag-list">{allTags.map((t) => <option key={t} value={t} />)}</datalist>
        <button onClick={() => tagVal && onRun("add_tag", tagVal)} disabled={busy || !tagVal} className="btn-secondary"
                data-testid="documents-bulk-add-tag">+ Tag</button>
        <button onClick={() => tagVal && onRun("remove_tag", tagVal)} disabled={busy || !tagVal} className="btn-secondary"
                data-testid="documents-bulk-remove-tag">− Tag</button>
      </div>

      <div className="flex items-center gap-1.5 ml-auto">
        <button onClick={() => onRun("set_visibility", "client")} disabled={busy} className="btn-secondary"
                data-testid="documents-bulk-share">Share with client</button>
        <button onClick={() => onRun("set_visibility", "firm")} disabled={busy} className="btn-secondary"
                data-testid="documents-bulk-unshare">Make internal</button>
        <button onClick={() => onRun("delete")} disabled={busy} className="btn-secondary"
                style={{ color: "var(--accent)" }}
                data-testid="documents-bulk-delete">
          <Trash2 size={12} /> Delete
        </button>
        <button onClick={onClear} className="btn-secondary" data-testid="documents-bulk-clear">
          <X size={12} /> Clear
        </button>
      </div>
    </div>
  );
}


function UploadButton({ uploading, clients, onUpload }) {
  const [pickedClient, setPickedClient] = useState("");
  const [vis, setVis] = useState("firm");
  const [folder, setFolder] = useState("");
  const [tags, setTags] = useState("");
  return (
    <div className="flex flex-wrap items-center gap-2 justify-end">
      <input value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="Folder (optional)"
        data-testid="document-upload-folder"
        className="bg-[var(--paper)] border border-[var(--border)] rounded-md px-3 py-2 text-[13px] w-36" />
      <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags, comma-sep"
        data-testid="document-upload-tags"
        className="bg-[var(--paper)] border border-[var(--border)] rounded-md px-3 py-2 text-[13px] w-40" />
      <select value={pickedClient} onChange={(e) => setPickedClient(e.target.value)}
        data-testid="document-upload-client"
        className="bg-[var(--paper)] border border-[var(--border)] rounded-md px-3 py-2 text-[13px]">
        <option value="">No client</option>
        {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
      </select>
      <select value={vis} onChange={(e) => setVis(e.target.value)}
        data-testid="document-upload-visibility"
        className="bg-[var(--paper)] border border-[var(--border)] rounded-md px-3 py-2 text-[13px]">
        <option value="firm">Internal</option>
        <option value="client">Share with client</option>
      </select>
      <label className={`btn-primary cursor-pointer ${uploading ? "opacity-60" : ""}`} data-testid="document-upload-btn">
        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        {uploading ? "Uploading…" : "Upload"}
        <input
          type="file"
          className="hidden"
          disabled={uploading}
          onChange={(e) => onUpload(e.target.files?.[0], {
            client_id: pickedClient || undefined,
            visibility: vis,
            folder: folder || undefined,
            tags: tags || undefined,
          })}
        />
      </label>
    </div>
  );
}
